'use client'

import { useEffect, useRef, useState } from 'react'
import { usePathname, useRouter } from 'next/navigation'

const START_DELAY_MS = 120
const FAKE_TICK_MS = 200
const FAKE_TARGET = 0.85
const HIDE_DELAY_MS = 280
const MAX_VISIBLE_MS = 12000

function isSameOriginInternalLinkClick(e: MouseEvent): HTMLAnchorElement | null {
  if (e.defaultPrevented) return null
  if (e.button !== 0) return null
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return null
  const target = e.target as Element | null
  if (!target) return null
  const anchor = target.closest('a') as HTMLAnchorElement | null
  if (!anchor) return null
  if (anchor.target && anchor.target !== '' && anchor.target !== '_self') return null
  if (anchor.hasAttribute('download')) return null
  const href = anchor.getAttribute('href')
  if (!href) return null
  if (href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return null
  try {
    const url = new URL(anchor.href, window.location.href)
    if (url.origin !== window.location.origin) return null
    if (url.pathname === window.location.pathname) return null
    return anchor
  } catch {
    return null
  }
}

export default function NavigationProgress() {
  const pathname = usePathname()
  const router = useRouter()

  const [visible, setVisible] = useState(false)
  const [progress, setProgress] = useState(0)
  const [completing, setCompleting] = useState(false)

  const startTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const tickTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const maxVisibleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const activeRef = useRef(false)

  const clearTimers = () => {
    if (startTimerRef.current) { clearTimeout(startTimerRef.current); startTimerRef.current = null }
    if (tickTimerRef.current) { clearInterval(tickTimerRef.current); tickTimerRef.current = null }
    if (hideTimerRef.current) { clearTimeout(hideTimerRef.current); hideTimerRef.current = null }
    if (maxVisibleTimerRef.current) { clearTimeout(maxVisibleTimerRef.current); maxVisibleTimerRef.current = null }
  }

  const startProgress = () => {
    if (activeRef.current) return
    activeRef.current = true
    if (hideTimerRef.current) { clearTimeout(hideTimerRef.current); hideTimerRef.current = null }
    setCompleting(false)
    setProgress(0)
    if (startTimerRef.current) clearTimeout(startTimerRef.current)
    startTimerRef.current = setTimeout(() => {
      setVisible(true)
      setProgress(0.08)
      if (tickTimerRef.current) clearInterval(tickTimerRef.current)
      tickTimerRef.current = setInterval(() => {
        setProgress(prev => {
          if (prev >= FAKE_TARGET) return prev
          const remaining = FAKE_TARGET - prev
          return prev + Math.max(0.005, remaining * 0.12)
        })
      }, FAKE_TICK_MS)
    }, START_DELAY_MS)
    if (maxVisibleTimerRef.current) clearTimeout(maxVisibleTimerRef.current)
    maxVisibleTimerRef.current = setTimeout(() => {
      if (activeRef.current) completeProgress()
    }, MAX_VISIBLE_MS)
  }

  const completeProgress = () => {
    if (!activeRef.current) return
    activeRef.current = false
    if (startTimerRef.current) { clearTimeout(startTimerRef.current); startTimerRef.current = null }
    if (tickTimerRef.current) { clearInterval(tickTimerRef.current); tickTimerRef.current = null }
    if (maxVisibleTimerRef.current) { clearTimeout(maxVisibleTimerRef.current); maxVisibleTimerRef.current = null }
    setCompleting(true)
    setProgress(1)
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current)
    hideTimerRef.current = setTimeout(() => {
      setVisible(false)
      setProgress(0)
      setCompleting(false)
      hideTimerRef.current = null
    }, HIDE_DELAY_MS)
  }

  // Intercept link clicks for SSR navigation start signal.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const anchor = isSameOriginInternalLinkClick(e)
      if (!anchor) return
      startProgress()
    }
    document.addEventListener('click', onClick, true)
    return () => document.removeEventListener('click', onClick, true)
  }, [])

  // Programmatic router.push/replace also triggers navigation; patch once per router instance.
  useEffect(() => {
    const patched = (router as any).__navProgressPatched
    if (patched) return
    const originalPush = router.push
    const originalReplace = router.replace
    router.push = ((href: string, options?: any) => {
      try {
        const url = new URL(href, window.location.href)
        if (url.origin === window.location.origin && url.pathname !== window.location.pathname) {
          startProgress()
        }
      } catch {}
      return originalPush(href, options)
    }) as typeof router.push
    router.replace = ((href: string, options?: any) => {
      try {
        const url = new URL(href, window.location.href)
        if (url.origin === window.location.origin && url.pathname !== window.location.pathname) {
          startProgress()
        }
      } catch {}
      return originalReplace(href, options)
    }) as typeof router.replace
    ;(router as any).__navProgressPatched = true
    return () => {
      router.push = originalPush
      router.replace = originalReplace
      ;(router as any).__navProgressPatched = false
    }
  }, [router])

  // When pathname changes, the new page has rendered — complete.
  useEffect(() => {
    completeProgress()
  }, [pathname])

  useEffect(() => () => clearTimers(), [])

  if (!visible) return null

  return (
    <div
      aria-hidden
      className="fixed top-0 left-0 right-0 z-[9999] pointer-events-none"
      style={{ height: 2 }}
    >
      <div
        className="h-full origin-left bg-gradient-to-r from-primary via-primary to-primary/80 shadow-[0_0_10px_rgba(0,0,0,0.15)]"
        style={{
          transform: `scaleX(${progress})`,
          transition: completing
            ? 'transform 200ms ease-out, opacity 200ms ease-in 100ms'
            : 'transform 200ms ease-out',
          opacity: completing ? 0 : 1,
          willChange: 'transform, opacity',
        }}
      />
    </div>
  )
}
